import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Loader2, Save } from 'lucide-react';
import { API, EMPTY_KB, EMPTY_CRITERION } from '../utils';
import { CriterionRow } from './CriterionRow';

/**
 * Full-featured "New KB" form used inside the admin KB list. Handles create
 * only — editing is done inline by the KBCard component.
 */
export const CreateKBForm = ({ token, domainId, onCreated, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY_KB });
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const addCriterion = () => setForm(f => ({ ...f, criteria: [...f.criteria, { ...EMPTY_CRITERION }] }));
  const updateCriterion = (i, k, v) => setForm(f => {
    const c = [...f.criteria]; c[i] = { ...c[i], [k]: v }; return { ...f, criteria: c };
  });
  const removeCriterion = i => setForm(f => ({ ...f, criteria: f.criteria.filter((_, idx) => idx !== i) }));

  const handleCreate = async () => {
    if (!form.name || !form.call_type) { toast.error('Name and call type are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, domain_id: domainId || null };
      await axios.post(`${API}/knowledge-bases`, payload, { headers });
      toast.success('Knowledge base created');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create KB');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white/5 border border-teal-500/30 rounded-xl p-5 space-y-4">
      <h3 className="text-white font-semibold flex items-center gap-2"><Plus className="w-4 h-4 text-teal-400" />New Knowledge Base
        {domainId && <span className="text-xs text-teal-400/60 font-normal">— domain-specific</span>}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-white/40 block mb-1">Name</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Sales — New Vehicles"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50" />
        </div>
        <div>
          <label className="text-xs text-white/40 block mb-1">Call / Coaching Type (identifier)</label>
          <input value={form.call_type} onChange={e => setForm(f => ({ ...f, call_type: e.target.value }))}
            placeholder="e.g. sales_new, it_technical"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-white/40 block mb-1">Description</label>
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-teal-500/50" />
        </div>
        <div>
          <label className="text-xs text-white/40 block mb-1">Escalation Threshold (%)</label>
          <input type="number" min={0} max={100} value={form.threshold_score}
            onChange={e => setForm(f => ({ ...f, threshold_score: parseInt(e.target.value) || 70 }))}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500/50" />
        </div>
        <div>
          <label className="text-xs text-white/40 block mb-1">Escalation Emails (comma-sep)</label>
          <input value={(form.escalation_emails || []).join(', ')}
            onChange={e => setForm(f => ({ ...f, escalation_emails: e.target.value.split(',').map(x => x.trim()).filter(Boolean) }))}
            placeholder="manager@company.com"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50" />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-white/60 text-xs font-medium uppercase tracking-wide">Grading Criteria</p>
          <button onClick={addCriterion} className="flex items-center gap-1 text-teal-400 text-xs hover:text-teal-300"><Plus className="w-3.5 h-3.5" />Add Criterion</button>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
          {form.criteria.map((c, i) => (
            <CriterionRow key={`new-criterion-${i}`} crit={c} idx={i} onChange={updateCriterion} onRemove={removeCriterion} />
          ))}
          {form.criteria.length === 0 && <p className="text-white/20 text-xs text-center py-3">Click &quot;Add Criterion&quot; to start building the grading rubric</p>}
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={handleCreate} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-lg disabled:opacity-50"
          data-testid="create-kb-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Create Knowledge Base
        </button>
        <button onClick={onCancel} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 text-sm rounded-lg">Cancel</button>
      </div>
    </div>
  );
};
